import { Suspense, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TenantProvider } from "@/contexts/TenantProvider";
import { TenantAccessProvider } from "@/contexts/TenantAccessProvider";
import { ModuleGuard } from "@/components/auth/ModuleGuard";
import { api } from "@/lib/api";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { ThemeProvider } from "@/components/theme/ThemeProvider";

const Login = lazyWithRetry(() => import("./pages/Login"), "route-login");
const ActivateAccount = lazyWithRetry(
  () => import("./pages/ActivateAccount"),
  "route-activate-account",
);
const Dashboard = lazyWithRetry(
  () => import("./pages/Dashboard"),
  "route-dashboard",
);
const Inventario = lazyWithRetry(
  () => import("./pages/Inventario"),
  "route-inventario",
);
const InventoryImport = lazyWithRetry(
  () => import("./pages/InventoryImport"),
  "route-inventory-import",
);
const ProductCreate = lazyWithRetry(
  () => import("./pages/ProductCreate"),
  "route-product-create",
);
const ProductEdit = lazyWithRetry(
  () => import("./pages/ProductEdit"),
  "route-product-edit",
);
const ProductAddVariant = lazyWithRetry(
  () => import("./pages/ProductAddVariant"),
  "route-product-add-variant",
);
const Clientes = lazyWithRetry(
  () => import("./pages/Clientes"),
  "route-clientes",
);
const Cotizaciones = lazyWithRetry(
  () => import("./pages/Cotizaciones"),
  "route-cotizaciones",
);
const QuoteEditor = lazyWithRetry(
  () => import("./pages/QuoteEditor"),
  "route-quote-editor",
);
const SalesHistory = lazyWithRetry(
  () => import("./pages/SalesHistory"),
  "route-sales-history",
);
const Alertas = lazyWithRetry(
  () => import("./pages/Alertas"),
  "route-alertas",
);
const POS = lazyWithRetry(() => import("./pages/POS"), "route-pos");
const Caja = lazyWithRetry(() => import("./pages/Caja"), "route-caja");
const CentroAyuda = lazyWithRetry(
  () => import("./pages/CentroAyuda"),
  "route-centro-ayuda",
);
const Onboarding = lazyWithRetry(
  () => import("./pages/Onboarding"),
  "route-onboarding",
);
const Novedades = lazyWithRetry(
  () => import("./pages/Novedades"),
  "route-novedades",
);
const AnalyticsDashboardV2 = lazyWithRetry(
  () => import("./pages/AnalyticsV2/AnalyticsDashboard"),
  "route-analytics-v2",
);
const Configuracion = lazyWithRetry(
  () => import("./pages/Configuracion"),
  "route-configuracion",
);
const Suscripcion = lazyWithRetry(
  () => import("./pages/Suscripcion"),
  "route-suscripcion",
);
const ReleaseCenter = lazyWithRetry(
  () => import("./pages/saas/ReleaseCenter"),
  "route-saas-releases",
);
const ReleaseDetail = lazyWithRetry(
  () => import("./pages/saas/ReleaseDetail"),
  "route-saas-release-detail",
);
const SaaSSubscriptions = lazyWithRetry(
  () => import("./pages/saas/SubscriptionsList"),
  "route-saas-subscriptions",
);
const Precios = lazyWithRetry(
  () => import("./pages/Precios"),
  "route-precios",
);
const Finanzas = lazyWithRetry(
  () => import("./pages/Finanzas"),
  "route-finanzas",
);
const Bitacora = lazyWithRetry(
  () => import("./pages/Bitacora"),
  "route-bitacora",
);
const Sitios = lazyWithRetry(() => import("./pages/Sitios"), "route-sitios");
const TenantList = lazyWithRetry(
  () => import("./pages/saas/TenantList"),
  "route-saas-tenants",
);
const NewTenant = lazyWithRetry(
  () => import("./pages/saas/NewTenant"),
  "route-saas-new-tenant",
);
const TenantDetail = lazyWithRetry(
  () => import("./pages/saas/TenantDetail"),
  "route-saas-tenant-detail",
);
const SuspendedPage = lazyWithRetry(
  () => import("./pages/account/Suspended"),
  "route-account-suspended",
);
const NotFound = lazyWithRetry(
  () => import("./pages/NotFound"),
  "route-not-found",
);
const AppLayout = lazyWithRetry(async () => {
  const module = await import("./components/layout/AppLayout");
  return { default: module.AppLayout };
}, "layout-app");
const SaasLayout = lazyWithRetry(async () => {
  const module = await import("./components/layout/SaasLayout");
  return { default: module.SaasLayout };
}, "layout-saas");
const SaaSMetrics = lazyWithRetry(
  () => import("./pages/saas/Metrics"),
  "route-saas-metrics",
);
const AuditLogs = lazyWithRetry(
  () => import("./pages/saas/Audit"),
  "route-saas-audit",
);
const SaaSSuggestions = lazyWithRetry(
  () => import("./pages/saas/Suggestions"),
  "route-saas-suggestions",
);
const SaaSSettings = lazyWithRetry(
  () => import("./pages/saas/Settings"),
  "route-saas-settings",
);
const TablesPage = lazyWithRetry(
  () => import("./pages/restaurant/Tables"),
  "route-restaurant-tables",
);
const KitchenPage = lazyWithRetry(
  () => import("./pages/restaurant/Kitchen"),
  "route-restaurant-kitchen",
);
const NegativeStockReport = lazyWithRetry(
  () => import("./pages/NegativeStockReport"),
  "route-negative-stock-report",
);
const KardexReport = lazyWithRetry(
  () => import("./pages/KardexReport"),
  "route-kardex-report",
);
const CustomerDisplay = lazyWithRetry(
  () => import("./pages/customer-display/CustomerDisplay"),
  "route-customer-display",
);
const ChangePassword = lazyWithRetry(
  () => import("./pages/ChangePassword"),
  "route-change-password",
);

type GlobalApiError = Error & {
  response?: {
    status?: number;
    data?: {
      status?: number;
      code?: string;
    };
  };
};

const handleGlobalError = (error: Error) => {
  const apiError = error as GlobalApiError;
  const code = apiError.response?.data?.code;

  if (code === "TENANT_SUSPENDED") {
    window.location.href = "/account/suspended?reason=SUSPENDED";
  } else if (code === "TENANT_TERMINATED") {
    window.location.href = "/account/suspended?reason=TERMINATED";
  } else if (code === "TENANT_PAST_DUE") {
    window.location.href = "/account/suspended?reason=PAST_DUE";
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleGlobalError,
  }),
  mutationCache: new MutationCache({
    onError: handleGlobalError,
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const RouteFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const withLazyRoute = (element: ReactNode) => (
  <Suspense fallback={<RouteFallback />}>{element}</Suspense>
);

// Helper to decode JWT payload
const getTokenPayload = () => {
  const token = api.getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload;
  } catch {
    return null;
  }
};

// Protected Route wrapper
const ProtectedRoute = () => {
  const token = api.getToken();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
};

// SuperAdmin Route - only accessible if isSuperAdmin
const SuperAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const payload = getTokenPayload();
  if (!payload?.isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Owner Route - tenant billing is OWNER-only (SuperAdmins pass through too, e.g.
// while impersonating). Anyone else is bounced back to the dashboard.
const OwnerRoute = ({ children }: { children: React.ReactNode }) => {
  const user = api.getCurrentUser() as { role?: string } | null;
  const payload = getTokenPayload();
  if (user?.role === "OWNER" || payload?.isSuperAdmin) {
    return <>{children}</>;
  }
  return <Navigate to="/dashboard" replace />;
};

const router = createBrowserRouter([
  {
    path: "/login",
    element: withLazyRoute(<Login />),
  },
  {
    path: "/activate-account",
    element: withLazyRoute(<ActivateAccount />),
  },
  {
    path: "/account/suspended",
    element: withLazyRoute(<SuspendedPage />),
  },
  {
    path: "/customer-display",
    element: withLazyRoute(<CustomerDisplay />),
  },
  {
    path: "/change-password",
    element: <ProtectedRoute />,
    children: [{ index: true, element: withLazyRoute(<ChangePassword />) }],
  },
  {
    path: "/",
    element: <ProtectedRoute />,
    children: [
      {
        path: "/saas",
        element: withLazyRoute(
          <SuperAdminRoute>
            <SaasLayout />
          </SuperAdminRoute>,
        ),
        children: [
          { path: "", element: <Navigate to="tenants" replace /> },
          { path: "tenants", element: withLazyRoute(<TenantList />) },
          { path: "tenants/new", element: withLazyRoute(<NewTenant />) },
          { path: "tenants/:id", element: withLazyRoute(<TenantDetail />) },
          {
            path: "tenants/:id/inventory-import",
            element: withLazyRoute(<InventoryImport />),
          },
          { path: "subscriptions", element: withLazyRoute(<SaaSSubscriptions />) },
          { path: "releases", element: withLazyRoute(<ReleaseCenter />) },
          { path: "releases/:id", element: withLazyRoute(<ReleaseDetail />) },
          { path: "metrics", element: withLazyRoute(<SaaSMetrics />) },
          { path: "audit", element: withLazyRoute(<AuditLogs />) },
          { path: "suggestions", element: withLazyRoute(<SaaSSuggestions />) },
          { path: "settings", element: withLazyRoute(<SaaSSettings />) },
          { path: "*", element: withLazyRoute(<NotFound />) },
        ],
      },
      {
        element: withLazyRoute(
          <AppLayout>
            <Outlet />
          </AppLayout>,
        ),
        children: [
          { path: "/", element: <Navigate to="/dashboard" replace /> },
          { path: "chat/*", element: <Navigate to="/dashboard" replace /> },
          { path: "dashboard", element: withLazyRoute(<Dashboard />) },
          {
            // Catálogo module ("lo que vendo"): Productos · Categorías · Publicación.
            // Same page as Inventario, rendered in catalog section. Keeps the old
            // /catalogo (storefront) access for INVENTORY or MENU tenants.
            path: "catalogo",
            element: withLazyRoute(
              <ModuleGuard module={["INVENTORY", "MENU"]}>
                <Inventario section="catalog" />
              </ModuleGuard>,
            ),
          },
          {
            // Canonical product-create home. Replaces /inventario/nuevo (redirected
            // below). INVENTORY-guarded to match the previous create route exactly.
            path: "catalogo/productos/nuevo",
            element: withLazyRoute(
              <ModuleGuard module="INVENTORY">
                <ProductCreate />
              </ModuleGuard>,
            ),
          },
          {
            // Product-level edit (photos/camera, rich description, classification).
            path: "catalogo/productos/:productId/editar",
            element: withLazyRoute(
              <ModuleGuard module="INVENTORY">
                <ProductEdit />
              </ModuleGuard>,
            ),
          },
          {
            path: "inventario",
            element: withLazyRoute(
              <ModuleGuard module="INVENTORY">
                <Inventario section="inventory" />
              </ModuleGuard>,
            ),
          },
          {
            path: "inventario/importar-csv",
            element: withLazyRoute(
              <ModuleGuard module="INVENTORY">
                <InventoryImport />
              </ModuleGuard>,
            ),
          },
          {
            // Legacy create route — preserve bookmarks/internal links (§24).
            path: "inventario/nuevo",
            element: <Navigate to="/catalogo/productos/nuevo" replace />,
          },
          {
            path: "inventario/:productId/variante/nueva",
            element: withLazyRoute(
              <ModuleGuard module="INVENTORY">
                <ProductAddVariant />
              </ModuleGuard>,
            ),
          },
          {
            path: "clientes",
            element: withLazyRoute(
              <ModuleGuard module="CUSTOMERS">
                <Clientes />
              </ModuleGuard>,
            ),
          },
          {
            path: "cotizaciones",
            element: withLazyRoute(
              <ModuleGuard module="QUOTES">
                <Cotizaciones />
              </ModuleGuard>,
            ),
          },
          {
            path: "cotizaciones/new",
            element: withLazyRoute(
              <ModuleGuard module="QUOTES">
                <QuoteEditor />
              </ModuleGuard>,
            ),
          },
          {
            path: "cotizaciones/:id/edit",
            element: withLazyRoute(
              <ModuleGuard module="QUOTES">
                <QuoteEditor />
              </ModuleGuard>,
            ),
          },
          {
            path: "ventas",
            element: withLazyRoute(
              <ModuleGuard module="SALES_HISTORY">
                <SalesHistory />
              </ModuleGuard>,
            ),
          },
          {
            path: "alertas",
            element: withLazyRoute(
              <ModuleGuard module="REPORTS">
                <Alertas />
              </ModuleGuard>,
            ),
          },
          { path: "ayuda", element: withLazyRoute(<CentroAyuda />) },
          { path: "onboarding", element: withLazyRoute(<Onboarding />) },
          { path: "novedades", element: withLazyRoute(<Novedades />) },
          {
            path: "pos",
            element: withLazyRoute(
              <ModuleGuard module="POS">
                <POS />
              </ModuleGuard>,
            ),
          },
          {
            path: "restaurant/tables",
            element: withLazyRoute(
              <ModuleGuard module="TABLES">
                <TablesPage />
              </ModuleGuard>,
            ),
          },
          {
            path: "restaurant/kitchen",
            element: withLazyRoute(
              <ModuleGuard module="KITCHEN">
                <KitchenPage />
              </ModuleGuard>,
            ),
          },
          {
            path: "caja",
            element: withLazyRoute(
              <ModuleGuard module="CASH_REGISTER">
                <Caja />
              </ModuleGuard>,
            ),
          },
          {
            path: "reportes",
            element: withLazyRoute(
              <ModuleGuard module="REPORTS">
                <AnalyticsDashboardV2 />
              </ModuleGuard>,
            ),
          },
          {
            path: "reportes/stock-negativo",
            element: withLazyRoute(
              <ModuleGuard module="INVENTORY">
                <NegativeStockReport />
              </ModuleGuard>,
            ),
          },
          {
            path: "reportes/kardex",
            element: withLazyRoute(
              <ModuleGuard module="INVENTORY">
                <KardexReport />
              </ModuleGuard>,
            ),
          },
          {
            path: "precios",
            element: withLazyRoute(
              // PROMOTIONS (not SETTINGS) — cashier gets read-only access to
              // Promociones here; PricingTab itself hides the admin-only
              // sub-tabs (Mayoreo/Reglas/Actividad) for that role.
              <ModuleGuard module="PROMOTIONS">
                <Precios />
              </ModuleGuard>,
            ),
          },
          {
            path: "finanzas",
            element: withLazyRoute(
              <ModuleGuard module="FINANCE">
                <Finanzas />
              </ModuleGuard>,
            ),
          },
          {
            path: "bitacora",
            element: withLazyRoute(
              <ModuleGuard module="SETTINGS">
                <Bitacora />
              </ModuleGuard>,
            ),
          },
          {
            path: "sitios",
            element: withLazyRoute(
              <ModuleGuard module="SETTINGS">
                <Sitios />
              </ModuleGuard>,
            ),
          },
          {
            path: "configuracion",
            element: withLazyRoute(
              <ModuleGuard module="SETTINGS">
                <Configuracion />
              </ModuleGuard>,
            ),
          },
          {
            // Planes y Suscripción — ítem principal (NO bajo Configuración),
            // OWNER-only. La restricción por suspensión vive en AppLayout.
            path: "suscripcion",
            element: withLazyRoute(
              <OwnerRoute>
                <Suscripcion />
              </OwnerRoute>,
            ),
          },
          { path: "*", element: withLazyRoute(<NotFound />) },
        ],
      },
    ],
  },
]);

// Configure themes: default light, storage key 'theme'
const App = () => (
  <ThemeProvider
    defaultTheme="light"
    enableSystem={false}
    storageKey="theme"
    attribute="class"
  >
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <TenantAccessProvider>
          <TooltipProvider>
            <Sonner />
            <RouterProvider
              router={router}
              future={{ v7_startTransition: true }}
            />
          </TooltipProvider>
        </TenantAccessProvider>
      </TenantProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default Sentry.withProfiler(App);
